<script lang="ts">
  import { t } from '../lib/i18n.svelte'
  import { dev } from '../lib/dev.svelte'
  import { onDestroy, onMount, tick, untrack } from 'svelte'
  import { account } from '../lib/account.svelte'
  import { friends } from '../lib/friends.svelte'
  import { live } from '../lib/live.svelte'
  import {
    roomHost,
    replacementRoom,
    recoverSharedRoom,
    takeRoomRecovery,
  } from '../lib/room-navigation'
  import { SignalingClient, type SignalingStatus } from '../lib/signaling'
  import type { RoomTarget } from '../lib/room-target'
  import { effectiveAppearance, prefs, savePrefs, toggleAppearance } from '../lib/prefs.svelte'
  import {
    AudioWatchdog,
    VideoWatchdog,
    StatsSampler,
    ViewerConnection,
    type ViewerStats,
  } from '../lib/viewer-rtc'
  import Brand from '../lib/Brand.svelte'
  import Icon from '../lib/Icon.svelte'
  import ProblemNotice from '../lib/ProblemNotice.svelte'
  import { roomProblem, type RoomProblem } from '../lib/problems'
  import { navigate } from '../lib/router.svelte'
  import { isDesktopApp } from '../lib/platform'
  import MarkLayer from '../lib/MarkLayer.svelte'
  import { markFrom } from '../lib/marks'
  import Chat from '../lib/Chat.svelte'
  import type { ChatLine } from '../lib/Chat.svelte'
  import {
    decode,
    encodeHand,
    encodeReaction,
    REACTIONS,
    tone,
    type Reaction,
    type Social,
  } from '../lib/social'
  import {
    normalizeRoomCode,
    PROTOCOL_VERSION,
    ROOM_CODE_LEN,
    type IceServer,
    type PeerInfo,
    type ClientMessage,
    type ServerMessage,
  } from '../lib/protocol'

  // The viewer page has no shared header, so its own top bar carries the theme toggle.
  const isDark = $derived(effectiveAppearance() === 'dark')
  const switchLabel = $derived(isDark ? t('Switch to light mode') : t('Switch to dark mode'))

  let { code, target }: { code: string; target: RoomTarget } = $props()

  /**
   * Validated once per code: a malformed path never even opens a socket.
   *
   * `$derived`, not a plain `const`: navigating straight from `/r/AAAAAA` to
   * `/r/BBBBBB` reuses this component instance, and a captured prop would leave
   * the page showing the old room.
   */
  const inDesktopApp = isDesktopApp
  const roomCode = $derived(normalizeRoomCode(code))

  type Health = 'smooth' | 'fair' | 'poor'

  let displayName = $state(prefs.displayName)
  let password = $state('')
  let passwordRequired = $state(false)
  const profileName = $derived(
    account.signedIn
      ? friends.me?.name?.trim() || account.name?.trim() || prefs.displayName.trim()
      : '',
  )
  let passwordInput = $state<HTMLInputElement>()
  let status = $state<SignalingStatus>('idle')
  let problemKind = $state<RoomProblem | null>(null)
  let problemDetail = $state('')
  const problem = $derived(
    problemKind ? roomProblem(problemKind, problemDetail || undefined) : null,
  )
  let joined = $state(false)
  let hostName = $state<string | null>(null)
  let viewerCount = $state(0)
  let mediaNote = $state<string | null>(null)
  let videoEl = $state<HTMLVideoElement | null>(null)
  let stageEl = $state<HTMLDivElement | null>(null)
  let markLayer = $state<MarkLayer>()
  let marking = $state(false)
  let playing = $state(false)
  let needsUnmute = $state(false)
  let showStats = $state(false)
  let stats = $state<ViewerStats | null>(null)
  let health = $state<Health | null>(null)
  let healthOpen = $state(false)
  let healthEl = $state<HTMLDivElement | null>(null)

  let people = $state<PeerInfo[]>([])
  let hands = $state<string[]>([])
  let floaters = $state<{ id: number; reaction: Reaction; name: string; x: number }[]>([])
  let joinedAt = $state(0)
  let now = $state(Date.now())
  let copied = $state(false)

  let volume = $state(1)
  let muted = $state(false)
  let theater = $state(false)
  let fullscreen = $state(false)
  const canPip = typeof document !== 'undefined' && document.pictureInPictureEnabled === true

  let client: SignalingClient | null = null
  let hostId: string | null = null
  let selfId = $state<string | null>(null)
  let chat = $state<ChatLine[]>([])
  // The translucent chat over the video: the last few spoken lines, each dropped
  // after the CSS fade has finished. Separate from `chat`, which keeps everything.
  const OVERLAY_LINES = 6
  const OVERLAY_LIFETIME_MS = 12_000
  let overlayRows = $state<{ id: number; name: string; item: Social }[]>([])
  let overlayId = 0
  let typing = $state(false)
  let typingDraft = $state('')
  let typingInput = $state<HTMLInputElement | null>(null)
  let iceServers: IceServer[] = []

  let floaterSeq = 0
  let lastFreezeAt = 0
  let lastFreezeCount = 0

  let rtc: ViewerConnection | null = null
  let playbackAttempt = 0
  let playbackFailed = false
  let playbackRetryTimer: ReturnType<typeof setTimeout> | null = null
  let statsTimer: ReturnType<typeof setInterval> | null = null
  let presentedVideoFrames = 0
  let joinMessage: ClientMessage | null = null
  let joinRequested = $state(false)
  const joining = $derived(joinRequested && !joined && !problem && status !== 'closed')
  const roomStatus = $derived(
    problem?.terminal
      ? problemKind === 'ended'
        ? t('Share ended')
        : t('Room unavailable')
      : problemKind === 'connection' || problemKind === 'media'
        ? t('Reconnecting')
        : problem
          ? t('Not connected')
          : joining
            ? t('Joining…')
            : joined
              ? t('Watching')
              : t('Ready to join'),
  )
  const problemBackLabel = $derived(
    problemKind === 'protocol_mismatch'
      ? inDesktopApp
        ? t('Check for updates')
        : t('Refresh page')
      : problemKind === 'connection' || problemKind === 'media'
        ? joined
          ? t('Leave the sofa')
          : t('Cancel')
        : t('Back to home'),
  )
  let joinTimer: ReturnType<typeof setTimeout> | null = null

  function cancelJoinTimer() {
    if (joinTimer) clearTimeout(joinTimer)
    joinTimer = null
  }
  let watchedHost = $state<string | null>(null)
  // Sharing a whole screen captures system sound, which includes this room.
  const echoRisk = $derived(inDesktopApp && live.room != null && live.room.monitor !== null)
  let echoDismissed = $state(false)

  onMount(() => {
    const pending = takeRoomRecovery(target)
    if (pending) {
      displayName = pending.name
      password = pending.password
      connectRoom()
    }
  })

  $effect(() => {
    if (account.restoring || !account.signedIn || joinRequested || !profileName) return
    if (!friends.me && friends.status === 'connecting') return
    displayName = profileName.slice(0, 32)
    untrack(connectRoom)
  })

  $effect(() => {
    if (!joinRequested) return
    const host = watchedHost ?? roomHost(friends.friends, target)
    if (!host) return
    watchedHost = host
    const next = replacementRoom(friends.friends, host, target)
    if (next) recoverSharedRoom(next, displayName, password)
  })
  let recoveryTimer: ReturnType<typeof setTimeout> | null = null
  let recoveryAttempts = 0
  const DISCONNECTED_GRACE_MS = 5000
  const MAX_RECOVERY_DELAY_MS = 15000
  /** Past this many people the list becomes a grid of avatars. */
  const LIST_LIMIT = 5

  /** Every sample of this session, for measurement from the console or a test. */
  const statsHistory: ViewerStats[] = []
  /** About ten minutes at one sample a second; enough to measure from the console. */
  const MAX_STATS_HISTORY = 600
  if (dev.on) {
    ;(window as unknown as { __sshareStats: ViewerStats[] }).__sshareStats = statsHistory
  }

  const healthLabel: Record<Health, string> = {
    smooth: t('Smooth'),
    fair: t('Bumpy'),
    poor: t('Struggling'),
  }

  const healthTitle: Record<Health, string> = {
    smooth: t('Smooth stream'),
    fair: t('Bumpy stream'),
    poor: t('Struggling stream'),
  }

  const reactionLabel: Record<Reaction, string> = {
    heart: t('Heart'),
    cheer: t('Cheer'),
    laugh: t('Laugh'),
    fire: t('Fire'),
    sad: t('Aww'),
  }

  const handUp = $derived(selfId != null && hands.includes(selfId))
  const grid = $derived(people.length > LIST_LIMIT)

  /**
   * Rough one-way delay a viewer feels: half the network round trip plus the time
   * frames wait to be played and decoded. Not glass to glass (capture and encode
   * happen on the host), but it moves with everything the viewer can affect.
   */
  const delayMs = $derived(
    stats ? (stats.rttMs ?? 0) / 2 + stats.jitterBufferMs + stats.decodeMs : null,
  )
  const droppedPct = $derived.by(() => {
    if (!stats) return null
    const seen = stats.framesDecoded + stats.framesDropped
    return seen > 0 ? (stats.framesDropped / seen) * 100 : 0
  })

  function sendChat(text: string) {
    client?.send({ type: 'relay_chat', text })
  }

  function react(reaction: Reaction) {
    sendChat(encodeReaction(reaction))
  }

  function toggleHand() {
    sendChat(encodeHand(!handUp))
  }

  /** A reaction drifting up over the video, for everyone watching. */
  function float(reaction: Reaction, name: string) {
    const id = ++floaterSeq
    floaters.push({ id, reaction, name, x: Math.round(Math.random() * 60) })
    if (floaters.length > 12) floaters.shift()
    setTimeout(() => {
      floaters = floaters.filter((item) => item.id !== id)
    }, 2800)
  }

  async function copyInvite() {
    try {
      await navigator.clipboard.writeText(target.shareUrl)
      copied = true
      setTimeout(() => (copied = false), 1800)
    } catch {
      // Clipboard blocked (insecure origin, permissions): nothing useful to do.
    }
  }

  function initial(name: string): string {
    return (name.trim()[0] ?? '?').toUpperCase()
  }

  function minutesAgo(since: number): string {
    const minutes = Math.max(0, Math.floor((now - since) / 60000))
    return minutes < 1 ? t('just now') : t('{n} min ago', { n: minutes })
  }

  function join(event: SubmitEvent) {
    event.preventDefault()
    connectRoom()
  }

  function connectRoom() {
    if (!roomCode) return
    if (profileName) displayName = profileName.slice(0, 32)
    joinRequested = true

    // Presence may have changed while the name/password form was open. Resolve
    // it before opening a socket so a stale address is never retried first.
    const host = watchedHost ?? roomHost(friends.friends, target)
    const next = host ? replacementRoom(friends.friends, host, target) : null
    if (next) {
      recoverSharedRoom(next, displayName, password)
      return
    }

    problemKind = null
    problemDetail = ''
    mediaNote = null
    client?.close()
    cancelRecovery()
    closeMedia()
    prefs.displayName = displayName.trim()
    savePrefs()

    joinMessage = {
      type: 'viewer_join',
      code: roomCode,
      ...(password.trim() ? { password: password.trim() } : {}),
      name: displayName.trim() || 'Guest',
      protocol_version: PROTOCOL_VERSION,
    }

    client = new SignalingClient(target.signalUrl, {
      handlers: {
        onStatus: (next, detail) => {
          status = next
          if (next === 'reconnecting') {
            cancelJoinTimer()
            problemKind = 'connection'
            problemDetail = detail ?? ''
          }
        },
        // Fires again after a reconnect, which is why the join message is sent
        // here rather than once after `connect()`.
        onOpen: () => {
          problemKind = null
          sendJoin()
        },
        onMessage: handleMessage,
        onError: (detail) => {
          problemKind = 'connection'
          problemDetail = detail
        },
      },
    })

    client.connect()
  }

  function handleMessage(message: ServerMessage) {
    switch (message.type) {
      case 'room_joined':
        cancelJoinTimer()
        problemKind = null
        joined = true
        hostId = message.host.id
        selfId = message.peer.id
        iceServers = message.ice_servers
        hostName = message.host.name
        viewerCount = message.peers.filter((peer) => peer.role === 'viewer').length + 1
        people = [
          message.host,
          ...message.peers.filter(
            (peer) => peer.id !== message.host.id && peer.id !== message.peer.id,
          ),
          message.peer,
        ]
        hands = []
        joinedAt = Date.now()
        mediaNote = t('In the room. Waiting for the host to send video.')
        break

      case 'peer_joined':
        viewerCount += 1
        if (!people.some((person) => person.id === message.peer.id)) people.push(message.peer)
        break

      case 'peer_left':
        viewerCount = Math.max(1, viewerCount - 1)
        people = people.filter((person) => person.id !== message.peer_id)
        hands = hands.filter((id) => id !== message.peer_id)
        markLayer?.drop(message.peer_id)
        break

      case 'room_closed':
        cancelJoinTimer()
        cancelRecovery()
        closeMedia()
        joined = false
        problemKind = 'ended'
        problemDetail = ''
        // A finished share is a normal end state, not a broken transport to retry.
        client?.close()
        break

      case 'error':
        cancelJoinTimer()
        joined = false
        cancelRecovery()
        closeMedia()
        problemKind = message.code
        problemDetail = message.code
        client?.close()
        if (message.code === 'bad_password') {
          passwordRequired = true
          void tick().then(() => passwordInput?.focus())
        }
        break

      case 'signal':
        if (message.from !== hostId) break
        if (message.payload.type === 'offer') {
          // A fresh offer means a fresh session (first join, or the host restarted).
          closeMedia()
          rtc = new ViewerConnection(iceServers, sendToHost, {
            onStream: attachStream,
            onState: handleMediaState,
          })
          mediaNote = t('Connecting to the host…')
          startStats()
        }
        rtc?.handle(message.payload).catch((err: unknown) => {
          problemKind = 'media'
          problemDetail = String(err)
          scheduleRecovery(recoveryDelayMs())
        })
        break

      case 'chat': {
        chat.push(message)
        const item = decode(message.text)
        if (item.kind !== 'hand' || message.from !== selfId) {
          const id = overlayId++
          overlayRows = [
            ...overlayRows.slice(-(OVERLAY_LINES - 1)),
            { id, name: message.name, item },
          ]
          setTimeout(() => {
            if (!typing) overlayRows = overlayRows.filter((row) => row.id !== id)
          }, OVERLAY_LIFETIME_MS)
        }
        if (item.kind === 'react') {
          float(item.reaction, message.from === selfId ? t('You') : message.name)
        }
        if (item.kind === 'hand') {
          hands = hands.filter((id) => id !== message.from)
          if (item.up) hands.push(message.from)
        }
        break
      }

      case 'mark': {
        const mark = markFrom(message)
        // The host's marks carry its peer id here but "host" on its own screen; use one id so the colour matches.
        if (mark) markLayer?.apply(mark.from === hostId ? { ...mark, from: 'host' } : mark)
        break
      }

      case 'room_created':
      case 'pong':
        break
    }
  }

  function sendToHost(payload: Parameters<ViewerConnection['handle']>[0]) {
    if (hostId) client?.send({ type: 'signal', to: hostId, payload })
  }

  function attachStream(stream: MediaStream) {
    if (!videoEl) return
    if (videoEl.srcObject !== stream) videoEl.srcObject = stream
    void startPlayback()
  }

  function cancelPlayback() {
    playbackAttempt += 1
    if (playbackRetryTimer) clearTimeout(playbackRetryTimer)
    playbackRetryTimer = null
  }

  async function startPlayback(retryInterrupted = true) {
    cancelPlayback()
    const attempt = playbackAttempt
    const element = videoEl
    const stream = element?.srcObject
    if (!element || !stream) return
    const current = () =>
      playbackAttempt === attempt && videoEl === element && element.srcObject === stream

    // Apply this before play(), rather than waiting for Svelte's next effect.
    element.muted = muted
    try {
      await element.play()
      if (!current()) return
      playing = true
      if (!muted) needsUnmute = false
      if (playbackFailed && rtc?.pc.connectionState === 'connected') {
        cancelRecovery()
        problemKind = null
        problemDetail = ''
        mediaNote = null
        recoveryAttempts = 0
      }
      playbackFailed = false
    } catch (error) {
      if (!current()) return
      const name = error instanceof Error ? error.name : ''
      if (name === 'NotAllowedError' && !element.muted) {
        // Only an actual autoplay refusal should switch to silent playback.
        muted = true
        needsUnmute = true
        void startPlayback()
      } else if (name === 'AbortError' && retryInterrupted) {
        // Track arrival or a source change can interrupt an in-flight play().
        playbackRetryTimer = setTimeout(() => {
          playbackRetryTimer = null
          if (current()) void startPlayback(false)
        }, 100)
      } else {
        playbackFailed = true
        problemKind = 'media'
        problemDetail = String(error)
        scheduleRecovery(recoveryDelayMs())
      }
    }
  }

  function unmute() {
    muted = false
    needsUnmute = false
    void startPlayback()
  }

  function toggleMute() {
    muted = !muted
    if (!muted) needsUnmute = false
    void startPlayback()
  }

  function togglePip() {
    if (!videoEl) return
    if (document.pictureInPictureElement) void document.exitPictureInPicture()
    else void videoEl.requestPictureInPicture().catch(() => {})
  }

  function toggleFullscreen() {
    if (document.fullscreenElement) void document.exitFullscreen()
    else void stageEl?.requestFullscreen().catch(() => {})
  }

  function openTyping() {
    typing = true
    queueMicrotask(() => typingInput?.focus())
  }

  function closeTyping(send: boolean) {
    const text = typingDraft.trim()
    if (send && text) sendChat(text)
    typing = false
    typingDraft = ''
  }

  function onTypingKey(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      event.preventDefault()
      closeTyping(true)
    } else if (event.key === 'Escape') {
      event.preventDefault()
      closeTyping(false)
    }
  }

  function onKey(event: KeyboardEvent) {
    const target = event.target as HTMLElement | null
    if (target && /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)) return
    if (event.key === 'Escape') {
      healthOpen = false
      marking = false
    }
    if (!playing || event.ctrlKey || event.metaKey || event.altKey) return
    if (event.key === 'Enter' && prefs.viewerChatOverlay) {
      event.preventDefault()
      openTyping()
    } else if (event.key === 'f') toggleFullscreen()
    else if (event.key === 't') theater = !theater
    else if (event.key === 'm') toggleMute()
    else if (event.key === 'd') marking = !marking
  }

  function startStats() {
    stopStats()
    const sampler = new StatsSampler()
    const audioWatchdog = new AudioWatchdog()
    const videoWatchdog = new VideoWatchdog()
    let sampling = false
    statsTimer = setInterval(async () => {
      const connection = rtc
      if (!connection || sampling) return
      sampling = true
      try {
        const sample = await sampler.sample(connection.pc)
        if (rtc !== connection || !sample) return
        stats = sample
        if (dev.on) {
          statsHistory.push(sample)
          if (statsHistory.length > MAX_STATS_HISTORY) statsHistory.shift()
        }
        health = judge(sample)
        const audible = playing && !muted && volume > 0 && !needsUnmute
        const connected = connection.pc.connectionState === 'connected'
        const presented =
          typeof videoEl?.requestVideoFrameCallback === 'function' ? presentedVideoFrames : null
        sample.framesPresented = presented
        const repair = videoWatchdog.check(sample, connected, presented)
        if (repair) console.warn('Video progress stalled', { repair, ...sample })
        if (repair === 'keyframe') connection.requestKeyframe()
        if (repair === 'playback') void startPlayback()
        if (repair === 'reconnect') {
          problemKind = 'media'
          mediaNote = t('Connection interrupted; recovering…')
          scheduleRecovery(recoveryDelayMs())
        }
        const stream = videoEl?.srcObject as MediaStream | null
        if (connected && stream?.getVideoTracks().some((track) => track.readyState === 'ended')) {
          problemKind = 'media'
          mediaNote = t('Connection interrupted; recovering…')
          scheduleRecovery(recoveryDelayMs())
        }
        const endedAudio = stream?.getAudioTracks().some((track) => track.readyState === 'ended')
        if (
          audioWatchdog.check(sample, audible && connected) ||
          (audible && connected && endedAudio)
        ) {
          problemKind = 'media'
          mediaNote = t('Audio playback stopped; reconnecting…')
          scheduleRecovery(recoveryDelayMs())
        }
      } catch (error) {
        // A closed/replaced peer can reject an outstanding getStats call.
        if (dev.on) console.warn('Stream progress sampling failed', error)
      } finally {
        sampling = false
      }
    }, 1000)
  }

  /**
   * One word for how the stream is doing, from what a viewer would notice:
   * a recent stall, a late network, or frames the decoder had to drop.
   */
  function judge(sample: ViewerStats): Health {
    const at = Date.now()
    // Connection setup always stalls a little; only judge a steady stream.
    if (sample.freezeCount > lastFreezeCount && sample.framesDecoded > 120) lastFreezeAt = at
    lastFreezeCount = sample.freezeCount
    const seen = sample.framesDecoded + sample.framesDropped
    const dropped = seen > 0 ? (sample.framesDropped / seen) * 100 : 0
    const stalledFor = at - lastFreezeAt
    if ((sample.rttMs ?? 0) > 300 || sample.receivedFps < 12 || stalledFor < 3000) return 'poor'
    if ((sample.rttMs ?? 0) > 120 || dropped > 3 || stalledFor < 10000) return 'fair'
    return 'smooth'
  }

  function stopStats() {
    if (statsTimer) clearInterval(statsTimer)
    statsTimer = null
  }

  function closeMedia() {
    cancelPlayback()
    playbackFailed = false
    stopStats()
    rtc?.close()
    rtc = null
    playing = false
    stats = null
    health = null
    lastFreezeAt = 0
    lastFreezeCount = 0
    if (videoEl) videoEl.srcObject = null
  }

  function handleMediaState(state: RTCPeerConnection['connectionState']) {
    if (state === 'connected' && !playbackFailed) {
      problemKind = null
      mediaNote = null
      cancelRecovery()
      recoveryAttempts = 0
    }
    if (state === 'failed') {
      problemKind = 'media'
      mediaNote = t('The connection to the host failed; reconnecting…')
      scheduleRecovery(recoveryDelayMs())
    }
    if (state === 'disconnected') {
      problemKind = 'media'
      // Often recovers by itself within a few seconds (a Wi-Fi roam); only
      // start over if it does not.
      mediaNote = t('Connection interrupted; recovering…')
      scheduleRecovery(DISCONNECTED_GRACE_MS)
    }
  }

  // For tests and bug reports: drive the recovery path without breaking a
  // real network (`__sshareDebug.mediaState('failed')` from the console).
  if (dev.on) {
    ;(window as unknown as { __sshareDebug: object }).__sshareDebug = {
      mediaState: handleMediaState,
    }
  }

  function sendJoin() {
    cancelJoinTimer()
    if (!joinMessage || !client?.send(joinMessage)) return
    joinTimer = setTimeout(() => {
      joinTimer = null
      cancelRecovery()
      closeMedia()
      joined = false
      problemKind = 'timeout'
      problemDetail = 'room_join_timeout'
      client?.close()
    }, 15_000)
  }

  /**
   * Start the media session over when the peer connection dies but signaling
   * is still up: leave the room and join again, which makes the host send a
   * fresh offer. (A signaling drop is handled by the client, which reconnects
   * and rejoins on its own.)
   */
  function recover() {
    recoveryTimer = null
    recoveryAttempts += 1
    closeMedia()
    mediaNote = t('Reconnecting to the host (attempt {n})…', { n: recoveryAttempts })
    client?.send({ type: 'leave_room' })
    sendJoin()
  }

  function scheduleRecovery(delayMs: number) {
    if (recoveryTimer) return
    recoveryTimer = setTimeout(recover, delayMs)
  }

  function cancelRecovery() {
    if (recoveryTimer) clearTimeout(recoveryTimer)
    recoveryTimer = null
  }

  /** 1 s, 2 s, 4 s … capped, so a host that is really gone is not hammered. */
  function recoveryDelayMs(): number {
    return Math.min(1000 * 2 ** recoveryAttempts, MAX_RECOVERY_DELAY_MS)
  }

  function leave() {
    cancelJoinTimer()
    joinRequested = false
    problemKind = null
    problemDetail = ''
    watchedHost = null
    cancelRecovery()
    closeMedia()
    client?.close()
    client = null
    joined = false
    hostName = null
    viewerCount = 0
    mediaNote = null
    people = []
    hands = []
    healthOpen = false
  }

  function goBack() {
    if (problemKind === 'connection' || problemKind === 'media') {
      leave()
      return
    }
    if (problemKind === 'protocol_mismatch') {
      leave()
      if (inDesktopApp) navigate('/settings')
      else location.reload()
      return
    }
    leave()
    navigate('/')
  }

  // Ask the browser for the smallest jitter buffer it will accept. The
  // property is non-standard: Chromium and Firefox honour it, others ignore it,
  // which is why it is set defensively rather than assumed. This is worth
  // roughly 50-100 ms of the glass-to-glass budget.
  $effect(() => {
    const element = videoEl
    presentedVideoFrames = 0
    if (!element || typeof element.requestVideoFrameCallback !== 'function') return
    let handle: number
    const onFrame = () => {
      presentedVideoFrames += 1
      handle = element.requestVideoFrameCallback(onFrame)
    }
    handle = element.requestVideoFrameCallback(onFrame)
    return () => element.cancelVideoFrameCallback(handle)
  })

  $effect(() => {
    if (!videoEl) return
    const element = videoEl as HTMLVideoElement & { playoutDelayHint?: number }
    element.playoutDelayHint = 0
  })

  $effect(() => {
    if (!videoEl) return
    videoEl.volume = volume
    videoEl.muted = muted
  })

  // The clock behind "joined 12 min ago"; a coarse tick is plenty.
  $effect(() => {
    const timer = setInterval(() => (now = Date.now()), 30000)
    return () => clearInterval(timer)
  })

  $effect(() => {
    const onChange = () => (fullscreen = document.fullscreenElement != null)
    document.addEventListener('fullscreenchange', onChange)
    return () => document.removeEventListener('fullscreenchange', onChange)
  })

  // In fullscreen, hide the overlay after a short pause in input, even while
  // the pointer rests on the stage or a control still holds focus.
  let idle = $state(false)
  $effect(() => {
    if (!fullscreen || !stageEl) {
      idle = false
      return
    }
    const stage = stageEl
    let timer: ReturnType<typeof setTimeout>
    const wake = () => {
      idle = false
      clearTimeout(timer)
      timer = setTimeout(() => (idle = true), 2500)
    }
    wake()
    stage.addEventListener('pointermove', wake)
    stage.addEventListener('pointerdown', wake)
    window.addEventListener('keydown', wake)
    return () => {
      clearTimeout(timer)
      stage.removeEventListener('pointermove', wake)
      stage.removeEventListener('pointerdown', wake)
      window.removeEventListener('keydown', wake)
      idle = false
    }
  })

  // Tear the connection down when this page goes away. A change of room code
  // recreates the component entirely (the route is keyed on the code), so
  // unmount really is the whole lifetime of the socket.
  onDestroy(() => {
    cancelJoinTimer()
    cancelRecovery()
    closeMedia()
    client?.close()
  })
</script>

<svelte:window
  onkeydown={onKey}
  onclick={(event) => {
    if (healthOpen && healthEl && !healthEl.contains(event.target as Node)) healthOpen = false
  }}
/>

<div class="viewer" class:viewer--theater={theater && joined}>
  {#if !roomCode}
    <div class="page stack">
      <div class="notice notice--error">
        <strong>{code}</strong> is not a room code. Codes are {ROOM_CODE_LEN} characters and never contain
        0, 1, O or I.
      </div>
      <div class="row">
        <a href="/"><button type="button">{t('Try another code')}</button></a>
      </div>
    </div>
  {:else}
    <header class="viewer-head">
      <div class="viewer-head__who">
        <Brand size={22} />
        <span class="viewer-head__sep"></span>
        {#if joined && hostName}
          <span>{t("{name}'s sofa", { name: hostName })}</span>
        {:else}
          <span class="faint">{t('Room {code}', { code: roomCode })}</span>
        {/if}
      </div>
      <div class="viewer-head__actions">
        <button
          type="button"
          class="theme-toggle"
          title={switchLabel}
          aria-label={switchLabel}
          onclick={toggleAppearance}
        >
          <Icon name={isDark ? 'sun' : 'moon'} size={18} />
        </button>
        {#if joined}
          {#if health && !problem}
            <div class="health" bind:this={healthEl}>
              <button
                type="button"
                class="health__pill health__pill--{health}"
                aria-expanded={healthOpen}
                onclick={() => (healthOpen = !healthOpen)}
              >
                <span class="dot"></span>
                {healthLabel[health]}{delayMs != null ? ` · ${Math.round(delayMs)} ms` : ''}
                <Icon name="chevron" size={16} />
              </button>
              {#if healthOpen && stats}
                <div class="health__card" role="dialog" aria-label={t('Stream health')}>
                  <div class="health__title health__title--{health}">
                    <Icon name={health === 'smooth' ? 'check' : 'warn'} size={20} />
                    {healthTitle[health]}
                  </div>
                  <dl class="health__rows">
                    <dt>{t('Path')}</dt>
                    <dd>
                      {stats.path === 'relay'
                        ? t('Through a relay')
                        : stats.path === 'direct'
                          ? t('Direct (peer-to-peer)')
                          : '–'}
                    </dd>
                    <dt>{t('Delay')}</dt>
                    <dd>{delayMs != null ? `${Math.round(delayMs)} ms` : '–'}</dd>
                    <dt>{t('Frame rate')}</dt>
                    <dd>{Math.round(stats.fps)} fps</dd>
                    <dt>{t('Resolution')}</dt>
                    <dd>{stats.width}×{stats.height}</dd>
                    <dt>{t('Dropped frames')}</dt>
                    <dd class:health__bad={(droppedPct ?? 0) > 3}>
                      {(droppedPct ?? 0).toFixed(1)}%
                    </dd>
                  </dl>
                  {#if dev.on}
                    <label class="health__toggle">
                      <input type="checkbox" bind:checked={showStats} />
                      {t('Technical details on the video')}
                    </label>
                  {/if}
                </div>
              {/if}
            </div>
          {:else}
            <span class="badge badge--ok" class:badge--warn={status === 'reconnecting'}>
              <span class="dot"></span>
              {roomStatus}
            </span>
          {/if}
          <button type="button" class="viewer-head__leave" onclick={leave}
            >{t('Leave the sofa')}</button
          >
        {:else}
          <span class="badge" class:badge--warn={status === 'reconnecting'}>
            <span class="dot"></span>
            {roomStatus}
          </span>
        {/if}
      </div>
    </header>

    {#if joined && echoRisk && !muted && !echoDismissed}
      <div class="viewer__notice">
        <div class="notice notice--warn row" style="justify-content: space-between">
          <span
            >{t(
              "You're sharing a whole screen, so this room's sound can loop back to your own friends. Use headphones or mute here.",
            )}</span
          >
          <span class="row">
            <button type="button" class="ghost" onclick={toggleMute}>{t('Mute')}</button>
            <button type="button" class="ghost" onclick={() => (echoDismissed = true)}
              >{t('Dismiss')}</button
            >
          </span>
        </div>
      </div>
    {/if}

    {#if problem && playing}
      <div class="viewer__notice">
        <ProblemNotice {problem} compact onRetry={connectRoom} />
      </div>
    {/if}

    <div
      class="viewer-grid"
      class:viewer-grid--focused={problem &&
        !joined &&
        problemKind !== 'bad_password' &&
        problemKind !== 'rate_limited'}
    >
      <div class="viewer-main">
        <div
          class="stage"
          bind:this={stageEl}
          class:stage--fullscreen={fullscreen}
          class:stage--idle={idle}
        >
          <video bind:this={videoEl} autoplay playsinline></video>
          <MarkLayer
            bind:this={markLayer}
            video={videoEl}
            active={marking && playing}
            {selfId}
            nameFor={(peer) =>
              people.find((person) => person.id === peer)?.name ?? hostName ?? undefined}
            send={(message) => client?.send(message)}
          />

          {#if playing}
            <div class="stage__top">
              <span class="stage__live"><span class="dot"></span>{t('LIVE')}</span>
              <span class="stage__count"
                ><Icon name="eye" size={16} />{t('{n} on the sofa', { n: viewerCount })}</span
              >
            </div>

            <div class="stage__floaters" aria-hidden="true">
              {#each floaters as item (item.id)}
                <div class="floater floater--{item.reaction}" style="right: {40 + item.x}px">
                  <Icon name={item.reaction} size={40} />
                  <span>{item.name}</span>
                </div>
              {/each}
            </div>

            {#if prefs.viewerChatOverlay}
              <div
                class="stage__chat"
                class:stage__chat--right={prefs.chatSide === 'right'}
                aria-hidden={!typing}
              >
                <ol class="stage__chat-list">
                  {#each overlayRows as row (row.id)}
                    <li class="stage__chat-row" class:stage__chat-row--held={typing}>
                      <strong>{row.name}</strong>
                      {#if row.item.kind === 'text'}
                        {row.item.text}
                      {:else if row.item.kind === 'react'}
                        <span class="note-pop chat__note-icon--{row.item.reaction}"
                          ><Icon name={row.item.reaction} size={28} /></span
                        >
                      {:else}
                        <span class="note-pop chat__note-icon--hand" class:note-wave={row.item.up}
                          ><Icon name="hand" size={28} />
                          {row.item.up ? t('raised a hand') : t('lowered their hand')}</span
                        >
                      {/if}
                    </li>
                  {/each}
                </ol>
                {#if typing}
                  <!-- mousedown is cancelled so the input keeps focus and the tray stays open. -->
                  <div class="stage__chat-tray" role="group" aria-label={t('Reactions')}>
                    {#each REACTIONS as reaction (reaction)}
                      <button
                        type="button"
                        class="stage__chat-react chat__note-icon--{reaction}"
                        aria-label={reactionLabel[reaction]}
                        title={reactionLabel[reaction]}
                        onmousedown={(e) => e.preventDefault()}
                        onclick={() => react(reaction)}><Icon name={reaction} size={20} /></button
                      >
                    {/each}
                    <button
                      type="button"
                      class="stage__chat-hand"
                      aria-pressed={handUp}
                      onmousedown={(e) => e.preventDefault()}
                      onclick={toggleHand}
                    >
                      <Icon name="hand" size={16} />
                      {handUp ? t('Lower hand') : t('Raise hand')}
                    </button>
                  </div>
                  <div class="chatfield">
                    <span class="chatfield__who">{t('You')}</span>
                    <input
                      bind:this={typingInput}
                      bind:value={typingDraft}
                      onkeydown={onTypingKey}
                      onblur={() => closeTyping(false)}
                      maxlength={500}
                      placeholder={t('Say something')}
                      aria-label={t('Chat message')}
                    />
                    <span class="chatfield__keys"><kbd>Enter</kbd> {t('Send')} <kbd>Esc</kbd></span>
                  </div>
                {/if}
              </div>
            {/if}

            <div class="stage__controls">
              <button
                type="button"
                class="ctl"
                aria-label={muted ? t('Unmute') : t('Mute')}
                onclick={toggleMute}><Icon name={muted ? 'muted' : 'volume'} size={22} /></button
              >
              <input
                class="ctl__volume"
                type="range"
                min="0"
                max="1"
                step="0.02"
                bind:value={volume}
                aria-label={t('Volume')}
              />
              <span class="ctl__space"></span>
              {#if dev.on}
                <button
                  type="button"
                  class="ctl"
                  aria-label={t('Stats')}
                  aria-pressed={showStats}
                  onclick={() => (showStats = !showStats)}><Icon name="stats" size={22} /></button
                >
              {/if}
              <button
                type="button"
                class="ctl"
                aria-label={t('Chat over video')}
                title={t('Chat over video (Enter to type)')}
                aria-pressed={prefs.viewerChatOverlay}
                onclick={() => {
                  prefs.viewerChatOverlay = !prefs.viewerChatOverlay
                  savePrefs()
                }}><Icon name="chat" size={22} /></button
              >
              <button
                type="button"
                class="ctl"
                aria-label={t('Point and draw')}
                title={t('Point and draw (D): move to point, click to ping, drag to draw')}
                aria-pressed={marking}
                onclick={() => (marking = !marking)}><Icon name="pen" size={22} /></button
              >
              {#if canPip}
                <button
                  type="button"
                  class="ctl"
                  aria-label={t('Picture in picture')}
                  onclick={togglePip}><Icon name="pip" size={22} /></button
                >
              {/if}
              <button
                type="button"
                class="ctl"
                aria-label={t('Theater mode')}
                aria-pressed={theater}
                onclick={() => (theater = !theater)}><Icon name="theater" size={22} /></button
              >
              <button
                type="button"
                class="ctl"
                aria-label={fullscreen ? t('Exit full screen') : t('Full screen')}
                onclick={toggleFullscreen}
                ><Icon name={fullscreen ? 'exit' : 'fullscreen'} size={22} /></button
              >
            </div>
          {/if}

          {#if playing && needsUnmute}
            <button type="button" class="stage__unmute primary" onclick={unmute}
              >{t('Unmute')}</button
            >
          {/if}

          {#if dev.on && showStats && stats}
            <div class="stage__stats">
              <div>
                {stats.width}×{stats.height} · {stats.codec.replace('video/', '')} · {stats.decoder}
              </div>
              <div>
                {stats.fps.toFixed(1)} fps · worst {stats.minFps.toFixed(1)} · received {stats.receivedFps.toFixed(
                  1,
                )}{stats.hidden ? ' (tab hidden)' : ''}
              </div>
              <div>
                freezes {stats.freezeCount} ({stats.totalFreezesMs.toFixed(0)} ms) · dropped {stats.framesDropped}
              </div>
              <div>
                jitter buffer {stats.jitterBufferMs.toFixed(1)} ms · decode {stats.decodeMs.toFixed(
                  1,
                )} ms
              </div>
              <div>
                {(stats.bitrateKbps / 1000).toFixed(2)} Mbps · lost {stats.packetsLost} · nack {stats.nackCount}
                · pli {stats.pliCount}
              </div>
              <div>
                rtt {stats.rttMs == null ? '–' : `${stats.rttMs.toFixed(0)} ms`} · audio concealed {stats.audioConcealedPct.toFixed(
                  1,
                )}%
              </div>
            </div>
          {/if}

          {#if problem && !playing}
            <div class="stage__placeholder stage__placeholder--problem">
              <ProblemNotice
                {problem}
                onRetry={connectRoom}
                onBack={joined ? undefined : goBack}
                backLabel={problemBackLabel}
              />
            </div>
          {:else if playing}
            <!-- the video is the whole stage once it plays -->
          {:else if !joined}
            <div class="stage__placeholder">
              <div>
                <p>
                  {joining || account.restoring || account.signedIn
                    ? t('Finding your seat…')
                    : t('Fill in your name to join the room.')}
                </p>
              </div>
            </div>
          {:else}
            <div class="stage__placeholder">
              <div>
                <p>{mediaNote ?? t('Connected.')}</p>
                {#if hostName}
                  <p class="faint">
                    {t('Hosted by {name} · {n} in the room', { name: hostName, n: viewerCount })}
                  </p>
                {/if}
              </div>
            </div>
          {/if}
        </div>

        {#if joined && hostName}
          <div class="viewer-info">
            <div class="viewer-info__who">
              <span class="avatar avatar--lg avatar--{tone(hostName)}">{initial(hostName)}</span>
              <div class="viewer-info__text">
                <span class="viewer-info__title">{t('{name} is sharing', { name: hostName })}</span>
                <span class="viewer-info__sub"
                  >{t('You joined {ago}', { ago: minutesAgo(joinedAt) })}</span
                >
              </div>
            </div>
            <div class="viewer-info__actions">
              {#each REACTIONS as reaction (reaction)}
                <button
                  type="button"
                  class="react react--{reaction}"
                  aria-label={reactionLabel[reaction]}
                  title={reactionLabel[reaction]}
                  onclick={() => react(reaction)}><Icon name={reaction} size={22} /></button
                >
              {/each}
              <span class="viewer-info__sep"></span>
              <button type="button" class="hand" aria-pressed={handUp} onclick={toggleHand}>
                <Icon name="hand" size={18} />
                {handUp ? t('Lower hand') : t('Raise hand')}
              </button>
            </div>
          </div>
        {/if}
      </div>

      {#if joined}
        <aside class="viewer-side">
          <div class="side__head">
            <h2>{t('On the sofa')}</h2>
            <button type="button" class="side__invite" onclick={copyInvite}>
              <Icon name="invite" size={16} />
              {copied ? t('Link copied') : t('Invite')}
            </button>
          </div>

          <ul class="people" class:people--grid={grid}>
            {#each people as person (person.id)}
              {@const me = person.id === selfId}
              {@const name = me ? t('You') : person.name}
              <li class="person" title={name}>
                <span
                  class="avatar avatar--{tone(person.name)}"
                  class:avatar--host={person.role === 'host'}
                  >{grid && me ? initial(person.name) : me ? t('You') : initial(person.name)}</span
                >
                <span class="person__text">
                  <span class="person__name"
                    >{name}{person.role === 'host' && !grid ? ` · ${t('host')}` : ''}</span
                  >
                  {#if !grid}
                    <span class="person__sub"
                      >{person.role === 'host' ? t('Sharing') : t('Watching')}</span
                    >
                  {/if}
                </span>
                {#if hands.includes(person.id)}
                  <span class="person__hand" title={t('Hand raised')}
                    ><Icon name="hand" size={18} /></span
                  >
                {/if}
              </li>
            {/each}
          </ul>

          {#if hands.length > 0}
            <div class="side__hands">
              <Icon name="hand" size={16} />
              {hands.length === 1 ? t('1 hand up') : t('{n} hands up', { n: hands.length })}
            </div>
          {/if}

          <hr class="side__rule" />
          <h2>{t('Chat')}</h2>
          <Chat bare messages={chat} {selfId} onSend={sendChat} onReact={react} />
        </aside>
      {:else if (!account.restoring && !account.signedIn && !problem) || problemKind === 'bad_password' || problemKind === 'rate_limited'}
        <form class="card stack viewer-join" onsubmit={join}>
          {#if !account.signedIn}
            <div class="field">
              <label for="name">{t('Your name')}</label>
              <input id="name" bind:value={displayName} placeholder={t('Guest')} maxlength="32" />
            </div>
          {/if}
          {#if passwordRequired}
            <div class="field">
              <label for="password">{t('Password')}</label>
              <input
                id="password"
                bind:this={passwordInput}
                type="password"
                bind:value={password}
                autocomplete="off"
                aria-invalid={problemKind === 'bad_password'}
                aria-describedby={problemKind === 'bad_password' ? 'password-help' : undefined}
              />
              {#if problemKind === 'bad_password'}<p class="faint" id="password-help">
                  {t('Check the password with the host, then try again.')}
                </p>{/if}
            </div>
          {/if}
          <button class="primary" type="submit" disabled={joining}>
            {joining ? t('Joining…') : t('Join room')}
          </button>
          {#if joining || problemKind === 'connection'}<button
              type="button"
              class="ghost"
              onclick={leave}>{t('Cancel')}</button
            >{/if}
        </form>
      {/if}
    </div>
  {/if}
</div>
